import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Alert,
  Tabs,
  Tab,
  Paper,
} from '@mui/material';
import { Analytics, Category, TrendingUp, Assessment } from '@mui/icons-material';
import { EmployeeLayout } from '@/components/Layout';
import { CategoryUsageReports, SystemRevenueAnalytics } from '@/components/admin';
import { useCategoryUsageReports } from '@/hooks/api';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`financial-reports-tabpanel-${index}`}
      aria-labelledby={`financial-reports-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ py: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `financial-reports-tab-${index}`,
    'aria-controls': `financial-reports-tabpanel-${index}`,
  };
}

const EmployeeFinancialReports: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [dateRange, setDateRange] = useState({
    startDate: new Date(Date.now() - 90 * 24 * 60 * 60 * 1000), // Last 90 days
    endDate: new Date()
  });

  // Use the category usage reports hook
  const {
    data: categoryData,
    loading: categoryLoading,
    error: categoryError,
    refresh: categoryRefresh,
    exportData: categoryExport
  } = useCategoryUsageReports({
    dateRange,
    autoRefresh: false
  });

  // Handle tab change
  const handleTabChange = useCallback((event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  }, []);

  // Handle date range change
  const handleDateRangeChange = useCallback((newDateRange: { startDate: Date; endDate: Date }) => {
    setDateRange(newDateRange);
  }, []);

  // Handle refresh
  const handleRefresh = useCallback(async () => {
    await categoryRefresh();
  }, [categoryRefresh]);

  // Handle export
  const handleExport = useCallback(async (format: 'csv' | 'excel') => {
    await categoryExport(format);
  }, [categoryExport]);

  return (
    <EmployeeLayout>
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Analytics sx={{ mr: 2, fontSize: 32, color: 'primary.main' }} />
          <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
            Financial Reports
          </Typography>
        </Box>

        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          Comprehensive financial analytics and reporting across all hosts and properties
        </Typography>

        <Paper elevation={1} sx={{ mb: 3 }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label="financial reports tabs"
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab
              icon={<Category />}
              label="Category Usage"
              {...a11yProps(0)}
              sx={{ minHeight: 72 }}
            />
            <Tab
              icon={<TrendingUp />}
              label="Revenue Analytics"
              {...a11yProps(1)}
              sx={{ minHeight: 72 }}
            />
            <Tab
              icon={<Assessment />}
              label="System Overview"
              {...a11yProps(2)}
              sx={{ minHeight: 72 }}
            />
          </Tabs>
        </Paper>

        <TabPanel value={tabValue} index={0}>
          <CategoryUsageReports
            data={categoryData || undefined}
            loading={categoryLoading}
            error={categoryError || undefined}
            dateRange={dateRange}
            onDateRangeChange={handleDateRangeChange}
            onRefresh={handleRefresh}
            onExport={handleExport}
          />
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <SystemRevenueAnalytics
            loading={false}
            error={undefined}
            dateRange={dateRange}
            onRefresh={() => {}}
          />
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                System Overview Reports
              </Typography>
              <Alert severity="info" sx={{ mb: 3 }}>
                System overview reporting functionality will be implemented in subsequent tasks.
              </Alert>
              <Typography variant="body2" color="text.secondary">
                This section will contain comprehensive system-wide financial reporting including:
              </Typography>
              <Box component="ul" sx={{ mt: 2 }}>
                <li>Host expense aggregation and analysis</li>
                <li>Property performance comparisons</li>
                <li>Financial data export tools</li>
                <li>Custom report generation</li>
              </Box>
            </CardContent>
          </Card>
        </TabPanel>
      </Box>
    </EmployeeLayout>
  );
};

export default EmployeeFinancialReports;